import {
    ActionIcon,
    Button,
    Group,
    Select,
    Stack,
    Switch,
    Text,
    Textarea,
    TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { CityFoodPlaceRO, CityFoodRO } from '@routellers/types/dist/city-food/city-food.ro';
import { GuideLocaleStatusEnum } from '@routellers/types/dist/city-guide/enum/guide-locale-status.enum';
import {
    GUIDE_STATUS_ORDER,
    GuideStatusEnum,
} from '@routellers/types/dist/city-guide/enum/guide-status.enum';
import { CONTENT_BASE_LANGUAGE } from '@routellers/types/dist/shared/content-base-language';
import { Languages } from '@routellers/types/dist/shared/enum/languages.enum';
import { IconTrash } from '@tabler/icons-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api, apiErrorMessage } from '../api';
import { DistrictSelect } from '../components/district-select';
import { EditorFooter, EntityEditorShell, LanguageChips, Section } from '../components/editor';
import { enumOptions } from '../i18n';
import { contentLanguagesForCountry } from '../utils/content-languages';

import { CitySearchSelect } from './city-search-select';

type Props = {
    opened: boolean;
    onClose: () => void;
    foodId: number | null;
    defaultCityId?: number | null;
    onSaved: () => void;
    onCreated?: (id: number) => void;
    onDeleted?: () => void;
};

type CardForm = {
    title: string;
    coverUrl: string;
    slug: string;
    cityId: number | null;
    districtId: number | null;
    status: GuideStatusEnum;
};

type LocaleDraft = {
    name: string;
    pitch: string;
    body: string;
    status: GuideLocaleStatusEnum;
};

type PlaceDraft = {
    establishmentId: number;
    title: string;
    orderNotes: Record<string, string>;
};

const statusColor: Record<GuideStatusEnum, string> = {
    [GuideStatusEnum.DRAFT]: 'gray',
    [GuideStatusEnum.PUBLISHED]: 'green',
};

const emptyCard = (cityId?: number | null): CardForm => ({
    title: '',
    coverUrl: '',
    slug: '',
    cityId: cityId ?? null,
    districtId: null,
    status: GuideStatusEnum.DRAFT,
});

const emptyLocale = (): LocaleDraft => ({
    name: '',
    pitch: '',
    body: '',
    status: GuideLocaleStatusEnum.EMPTY,
});

const placesFrom = (places: CityFoodPlaceRO[] | undefined): PlaceDraft[] =>
    [...(places || [])]
        .sort((a, b) => a.sort - b.sort)
        .map((place) => ({
            establishmentId: place.establishmentId,
            title: place.establishmentTitle || `#${place.establishmentId}`,
            orderNotes: { ...(place.orderNotes || {}) },
        }));

export const FoodEditor = ({
    opened,
    onClose,
    foodId,
    defaultCityId,
    onSaved,
    onCreated,
    onDeleted,
}: Props) => {
    const { t } = useTranslation();
    const isCreate = foodId == null;
    const [row, setRow] = useState<CityFoodRO | null>(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [tab, setTab] = useState('card');
    const [lang, setLang] = useState<Languages>(CONTENT_BASE_LANGUAGE);
    const [drafts, setDrafts] = useState<Record<string, LocaleDraft>>({});
    const [places, setPlaces] = useState<PlaceDraft[]>([]);
    const [establishmentOptions, setEstablishmentOptions] = useState<
        { value: string; label: string }[]
    >([]);
    const [addId, setAddId] = useState<string | null>(null);
    const form = useForm<CardForm>({ initialValues: emptyCard(defaultCityId) });

    const languages = row ? contentLanguagesForCountry(undefined) : [CONTENT_BASE_LANGUAGE];

    const applyRow = (next: CityFoodRO) => {
        setRow(next);
        form.setValues({
            title: next.title || '',
            coverUrl: next.coverUrl || '',
            slug: next.slug || '',
            cityId: next.cityId,
            districtId: next.districtId,
            status: next.status,
        });
        form.resetDirty();
        const nextDrafts: Record<string, LocaleDraft> = {};

        for (const language of contentLanguagesForCountry(undefined)) {
            const locale = (next.locales || []).find((item) => item.language === language);

            nextDrafts[language] = locale
                ? {
                      name: locale.name || '',
                      pitch: locale.pitch || '',
                      body: locale.body || '',
                      status: locale.status,
                  }
                : emptyLocale();
        }

        setDrafts(nextDrafts);
        setPlaces(placesFrom(next.places));
    };

    useEffect(() => {
        if (!opened) return;

        setTab('card');
        setLang(CONTENT_BASE_LANGUAGE);
        setAddId(null);

        if (foodId == null) {
            setRow(null);
            form.setValues(emptyCard(defaultCityId));
            form.resetDirty();
            setDrafts({});
            setPlaces([]);

            return;
        }

        let cancelled = false;

        setLoading(true);
        api.cityFoodsApi
            .getOne(foodId)
            .then(({ data }) => {
                if (!cancelled && data) applyRow(data);
            })
            .catch((error) => {
                notifications.show({ color: 'red', message: apiErrorMessage(error) });
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [opened, foodId]);

    useEffect(() => {
        const cityId = form.values.cityId;

        if (!opened || !cityId) {
            setEstablishmentOptions([]);

            return;
        }

        let cancelled = false;

        const loadOptions = async () => {
            const options: { value: string; label: string }[] = [];
            let page = 1;
            let totalPages = 1;
            do {
                const { data } = await api.cityEstablishmentsApi.query({
                    page,
                    limit: 100,
                    sort: 'title',
                    order: 'ASC',
                    where: { cityId },
                });
                if (cancelled) return;
                for (const item of data?.items || []) {
                    options.push({
                        value: String(item.id),
                        label:
                            item.locales?.find(
                                (locale) =>
                                    locale.language === CONTENT_BASE_LANGUAGE &&
                                    locale.name?.trim(),
                            )?.name || item.title,
                    });
                }
                totalPages = Number(data?.meta?.totalPages) || 1;
                page += 1;
            } while (page <= totalPages);
            setEstablishmentOptions(options);
        };
        void loadOptions().catch(() => {
            if (!cancelled) setEstablishmentOptions([]);
        });

        return () => {
            cancelled = true;
        };
    }, [opened, form.values.cityId]);

    const statusOptions = enumOptions(GUIDE_STATUS_ORDER, t, 'enums.guideStatus');
    const current = drafts[lang] || emptyLocale();
    const chips = useMemo(
        () =>
            languages.map((language) => {
                const item = drafts[language] || emptyLocale();
                let mark: 'filled' | 'reviewed' | 'empty' = 'empty';

                if (item.status === GuideLocaleStatusEnum.READY) mark = 'reviewed';
                else if (item.name.trim() || item.pitch.trim() || item.body.trim()) mark = 'filled';

                return { value: language, label: language.toUpperCase(), mark };
            }),
        [languages, drafts],
    );

    const patchDraft = (patch: Partial<LocaleDraft>) => {
        setDrafts((prev) => ({
            ...prev,
            [lang]: { ...(prev[lang] || emptyLocale()), ...patch },
        }));
    };

    const placesPayload = () =>
        places.map((place, index) => ({
            establishmentId: place.establishmentId,
            sort: index,
            orderNotes: Object.fromEntries(
                Object.entries(place.orderNotes)
                    .map(([key, value]) => [key, value.trim()])
                    .filter(([, value]) => value),
            ),
        }));

    const saveCard = async () => {
        const title = form.values.title.trim();

        if (!title || !form.values.cityId) {
            notifications.show({ color: 'red', message: t('pages.foods.editor.required') });

            return;
        }

        const readySaved = (row?.locales || []).some(
            (locale) => locale.status === GuideLocaleStatusEnum.READY,
        );

        if (form.values.status === GuideStatusEnum.PUBLISHED && !readySaved) {
            notifications.show({ color: 'red', message: t('pages.foods.editor.publishNeedsText') });

            return;
        }

        const payload = {
            title,
            coverUrl: form.values.coverUrl.trim() || null,
            slug: form.values.slug.trim() || null,
            cityId: form.values.cityId,
            districtId: form.values.districtId,
            status: form.values.status,
            places: isCreate ? undefined : placesPayload(),
        };

        setSaving(true);

        try {
            if (isCreate) {
                const { data } = await api.cityFoodsApi.create({ data: payload });

                notifications.show({ color: 'green', message: t('pages.foods.editor.created') });
                onSaved();
                if (data?.id) onCreated?.(data.id);
            } else {
                const { data } = await api.cityFoodsApi.update({ id: foodId!, data: payload });

                if (data) applyRow(data);
                notifications.show({ color: 'green', message: t('pages.foods.editor.saved') });
                onSaved();
            }
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setSaving(false);
        }
    };

    const saveLocale = async () => {
        if (!foodId) return;

        if (
            current.status === GuideLocaleStatusEnum.READY &&
            (!current.name.trim() || !current.pitch.trim())
        ) {
            notifications.show({ color: 'red', message: t('pages.foods.editor.readyNeedsCopy') });

            return;
        }

        setSaving(true);

        try {
            await api.cityFoodsApi.upsertLocale({
                id: foodId,
                data: {
                    language: lang,
                    name: current.name.trim(),
                    pitch: current.pitch.trim() || null,
                    body: current.body.trim() || null,
                    status: current.status,
                },
            });

            const { data } = await api.cityFoodsApi.update({
                id: foodId,
                data: { places: placesPayload() },
            });

            if (data) applyRow(data);
            notifications.show({ color: 'green', message: t('pages.foods.editor.saved') });
            onSaved();
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        if (!foodId || !row) return;
        if (!window.confirm(t('pages.foods.editor.confirmDelete', { title: row.title }))) return;

        setSaving(true);

        try {
            await api.cityFoodsApi.remove(foodId);
            notifications.show({ color: 'green', message: t('pages.foods.editor.deleted') });
            onDeleted?.();
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setSaving(false);
        }
    };

    const addPlace = () => {
        const id = Number(addId);

        if (!id || places.some((place) => place.establishmentId === id)) return;

        const option = establishmentOptions.find((item) => item.value === String(id));

        setPlaces((prev) => [
            ...prev,
            { establishmentId: id, title: option?.label || `#${id}`, orderNotes: {} },
        ]);
        setAddId(null);
    };

    return (
        <EntityEditorShell
            opened={opened}
            onClose={onClose}
            loading={loading}
            title={
                isCreate
                    ? t('pages.foods.editor.newTitle')
                    : row?.title || t('pages.foods.editor.fallbackTitle', { id: foodId })
            }
            subtitle={row?.cityTitle || undefined}
            status={
                row
                    ? {
                          label: t(`enums.guideStatus.${row.status}`),
                          color: statusColor[row.status],
                      }
                    : undefined
            }
            tabs={
                isCreate
                    ? [{ value: 'card', label: t('pages.foods.editor.tabs.card') }]
                    : [
                          { value: 'card', label: t('pages.foods.editor.tabs.card') },
                          { value: 'text', label: t('pages.foods.editor.tabs.text') },
                      ]
            }
            activeTab={tab}
            onTabChange={setTab}
            footer={
                <EditorFooter
                    onCancel={onClose}
                    onSave={tab === 'text' ? saveLocale : saveCard}
                    saving={saving}
                    left={
                        !isCreate && tab === 'card' ? (
                            <Button color='red' variant='light' onClick={remove}>
                                {t('pages.foods.editor.delete')}
                            </Button>
                        ) : undefined
                    }
                />
            }
        >
            {tab === 'card' && (
                <Stack gap='lg'>
                    <Section title={t('pages.foods.editor.identity')}>
                        <TextInput
                            label='Обложка блюда (HTTPS URL)'
                            {...form.getInputProps('coverUrl')}
                        />
                        <TextInput
                            label={t('pages.foods.editor.title')}
                            required
                            {...form.getInputProps('title')}
                        />
                        <TextInput
                            label={t('pages.foods.editor.slug')}
                            description={t('pages.foods.editor.slugHint')}
                            {...form.getInputProps('slug')}
                        />
                        <CitySearchSelect
                            label={t('pages.foods.editor.city')}
                            value={form.values.cityId}
                            onChange={(cityId) => {
                                form.setFieldValue('cityId', cityId);
                                if (cityId !== form.values.cityId)
                                    form.setFieldValue('districtId', null);
                            }}
                            initialLabel={row?.cityTitle}
                        />
                        <DistrictSelect
                            cityId={form.values.cityId}
                            value={form.values.districtId}
                            onChange={(districtId) => form.setFieldValue('districtId', districtId)}
                            description={t('pages.foods.editor.districtHint')}
                        />
                        <Select
                            label={t('pages.foods.editor.status')}
                            description={t('pages.foods.editor.statusHint')}
                            data={statusOptions}
                            {...form.getInputProps('status')}
                        />
                    </Section>
                </Stack>
            )}
            {tab === 'text' && !isCreate && (
                <Stack gap='lg'>
                    <LanguageChips
                        languages={chips}
                        value={lang}
                        onChange={(value) => setLang(value as Languages)}
                    />
                    <Section
                        title={t('pages.foods.editor.copyTitle')}
                        description={t('pages.foods.editor.copyHint')}
                    >
                        <TextInput
                            label={t('pages.foods.editor.name')}
                            value={current.name}
                            onChange={(event) => patchDraft({ name: event.currentTarget.value })}
                        />
                        <Textarea
                            label={t('pages.foods.editor.pitch')}
                            description={t('pages.foods.editor.pitchHint')}
                            minRows={4}
                            autosize
                            value={current.pitch}
                            onChange={(event) => patchDraft({ pitch: event.currentTarget.value })}
                        />
                        <Textarea
                            label='Статья о блюде'
                            description='Абзацы разделяй пустой строкой. Заголовки: ## и ###. Карточка заведения: [[establishment:123]] на отдельной строке.'
                            minRows={12}
                            autosize
                            value={current.body}
                            onChange={(event) => patchDraft({ body: event.currentTarget.value })}
                        />
                        <Group align='flex-end'>
                            <Select
                                label='Вставить заведение в статью'
                                searchable
                                data={establishmentOptions}
                                value={addId}
                                onChange={(value) => setAddId(value == null ? null : String(value))}
                                style={{ flex: 1 }}
                            />
                            <Button
                                disabled={!addId}
                                onClick={() => {
                                    patchDraft({
                                        body: `${current.body.trimEnd()}\n\n[[establishment:${addId}]]\n`,
                                    });
                                    setAddId(null);
                                }}
                            >
                                Вставить в конец
                            </Button>
                        </Group>
                        <Switch
                            label={t('pages.foods.editor.ready')}
                            description={t('pages.foods.editor.readyHint')}
                            checked={current.status === GuideLocaleStatusEnum.READY}
                            onChange={(event) =>
                                patchDraft({
                                    status: event.currentTarget.checked
                                        ? GuideLocaleStatusEnum.READY
                                        : GuideLocaleStatusEnum.EMPTY,
                                })
                            }
                        />
                    </Section>
                    <Section
                        title={t('pages.foods.editor.places')}
                        description={t('pages.foods.editor.placesHint')}
                    >
                        {places.map((place, index) => (
                            <Group key={place.establishmentId} align='flex-start' wrap='nowrap'>
                                <Stack gap={4} style={{ flex: 1 }}>
                                    <Text fw={550}>{place.title}</Text>
                                    <Textarea
                                        label={t('pages.foods.editor.orderNote', {
                                            lang: lang.toUpperCase(),
                                        })}
                                        minRows={2}
                                        autosize
                                        value={place.orderNotes[lang] || ''}
                                        onChange={(event) => {
                                            const value = event.currentTarget.value;

                                            setPlaces((prev) =>
                                                prev.map((item, itemIndex) =>
                                                    itemIndex === index
                                                        ? {
                                                              ...item,
                                                              orderNotes: {
                                                                  ...item.orderNotes,
                                                                  [lang]: value,
                                                              },
                                                          }
                                                        : item,
                                                ),
                                            );
                                        }}
                                    />
                                </Stack>
                                <ActionIcon
                                    color='red'
                                    variant='light'
                                    mt={28}
                                    onClick={() =>
                                        setPlaces((prev) =>
                                            prev.filter((_, itemIndex) => itemIndex !== index),
                                        )
                                    }
                                >
                                    <IconTrash size={16} />
                                </ActionIcon>
                            </Group>
                        ))}
                        <Group align='flex-end'>
                            <Select
                                label={t('pages.foods.editor.addPlace')}
                                data={establishmentOptions.filter(
                                    (option) =>
                                        !places.some(
                                            (place) =>
                                                String(place.establishmentId) === option.value,
                                        ),
                                )}
                                value={addId}
                                onChange={(value) => setAddId(value == null ? null : String(value))}
                                searchable
                                clearable
                                style={{ flex: 1 }}
                            />
                            <Button variant='light' disabled={!addId} onClick={addPlace}>
                                {t('pages.foods.editor.add')}
                            </Button>
                        </Group>
                    </Section>
                </Stack>
            )}
        </EntityEditorShell>
    );
};
